import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { PageLoader } from '../../components/Loader';
import {
  CalendarIcon,
  UserIcon,
  AcademicCapIcon,
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon,
  ArrowLeftIcon,
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://lavender-falcon-985378.hostingersite.com/api/v1';

export default function StudentAttendance() {
  const { id } = useParams();
  const { user } = useAuth();
  const [student, setStudent] = useState(null);
  const [attendance, setAttendance] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [currentStudentId, setCurrentStudentId] = useState(null);

  // Summary stats
  const [stats, setStats] = useState({
    present: 0,
    absent: 0,
    late: 0,
    total: 0,
  });

  // Step 1: Resolve student ID from URL or from authenticated user
  useEffect(() => {
    const fetchStudentId = async () => {
      if (id) {
        setCurrentStudentId(id);
        return;
      }

      if (!user) {
        setError('User not authenticated.');
        setLoading(false);
        return;
      }

      if (user.student_id) {
        setCurrentStudentId(user.student_id);
        return;
      }

      try {
        const token = localStorage.getItem('token');
        const headers = { Authorization: `Bearer ${token}` };
        const response = await axios.get(`${API_BASE_URL}/students/by-user/${user.id}`, { headers });
        if (response.data.success && response.data.data) {
          setCurrentStudentId(response.data.data.id);
        } else {
          setError('Could not find your student profile.');
          setLoading(false);
        }
      } catch (err) {
        console.error('Error fetching student ID:', err);
        setError(err.response?.data?.message || 'Could not retrieve your student information.');
        setLoading(false);
      }
    };

    fetchStudentId();
  }, [id, user]);

  // Step 2: Fetch student details and attendance using the resolved student ID
  useEffect(() => {
    if (!currentStudentId) return;

    const fetchData = async () => {
      try {
        setLoading(true);
        const token = localStorage.getItem('token');
        const headers = { Authorization: `Bearer ${token}` };

        // 1. Fetch student details
        const studentRes = await axios.get(`${API_BASE_URL}/students/${currentStudentId}`, { headers });
        if (studentRes.data.success) {
          setStudent(studentRes.data.data);
        } else {
          setError('Failed to load student details.');
        }

        // 2. Fetch attendance records
        const attendanceRes = await axios.get(`${API_BASE_URL}/students/${currentStudentId}/attendance`, { headers });
        if (attendanceRes.data.success) {
          setAttendance(attendanceRes.data.data || []);
          const records = attendanceRes.data.data || [];
          const present = records.filter(r => r.status?.toLowerCase() === 'present').length;
          const absent = records.filter(r => r.status?.toLowerCase() === 'absent').length;
          const late = records.filter(r => r.status?.toLowerCase() === 'late').length;
          setStats({ present, absent, late, total: records.length });
        } else {
          setError(attendanceRes.data.message || 'Failed to load attendance.');
        }
      } catch (err) {
        console.error('Error fetching attendance:', err);
        setError(err.response?.data?.message || 'Unable to fetch attendance. Please try again.');
        toast.error('Could not load attendance data.');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [currentStudentId]);

  // Filter attendance by date range
  const filteredAttendance = attendance.filter(record => {
    const recordDate = new Date(record.date);
    if (startDate && new Date(startDate) > recordDate) return false;
    if (endDate && new Date(endDate) < recordDate) return false;
    return true;
  });

  const formatDate = (dateStr) => {
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const StatusBadge = ({ status }) => {
    const statusLower = status?.toLowerCase() || '';
    const colors = {
      present: 'bg-green-100 text-green-800',
      absent: 'bg-red-100 text-red-800',
      late: 'bg-yellow-100 text-yellow-800',
    };
    const icons = {
      present: <CheckCircleIcon className="w-4 h-4 mr-1" />,
      absent: <XCircleIcon className="w-4 h-4 mr-1" />,
      late: <ClockIcon className="w-4 h-4 mr-1" />,
    };
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${colors[statusLower] || 'bg-gray-100 text-gray-800'}`}>
        {icons[statusLower] || null}
        {status || '—'}
      </span>
    );
  };

  if (loading) return <PageLoader />;

  if (error) {
    return (
      <div className="p-8 text-center">
        <div className="text-red-500 text-lg font-semibold">⚠️ {error}</div>
        <button
          onClick={() => window.location.reload()}
          className="mt-4 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Back button – only show if we have an ID from params */}
      {id ? (
        <div className="mb-6">
          <Link
            to={`/app/students/${id}`}
            className="inline-flex items-center text-primary-600 hover:text-primary-800"
          >
            <ArrowLeftIcon className="w-5 h-5 mr-2" />
            Back to Student Profile
          </Link>
        </div>
      ) : (
        <div className="mb-6">
          <Link
            to="/app/dashboard"
            className="inline-flex items-center text-primary-600 hover:text-primary-800"
          >
            <ArrowLeftIcon className="w-5 h-5 mr-2" />
            Back to Dashboard
          </Link>
        </div>
      )}

      {/* Student Header */}
      {student && (
        <div className="bg-white rounded-xl shadow-sm border border-primary-200 p-6 mb-8">
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex-shrink-0">
              <div className="w-16 h-16 rounded-full bg-primary-100 flex items-center justify-center">
                <UserIcon className="w-8 h-8 text-primary-600" />
              </div>
            </div>
            <div>
              <h1 className="text-2xl font-bold text-primary-900">
                {student.first_name} {student.middle_name || ''} {student.last_name}
              </h1>
              <div className="flex flex-wrap items-center gap-3 mt-1 text-sm text-primary-600">
                <span className="flex items-center">
                  <AcademicCapIcon className="w-4 h-4 mr-1" />
                  {student.class?.name || 'No class assigned'}
                </span>
                <span>•</span>
                <span>Admission: {student.admission_number}</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Stats Cards – unchanged */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <div className="bg-white rounded-lg shadow-sm border border-primary-200 p-4">
          <div className="flex items-center">
            <div className="p-2 bg-blue-100 rounded-lg">
              <CalendarIcon className="w-6 h-6 text-blue-600" />
            </div>
            <div className="ml-3">
              <p className="text-xs text-primary-500 uppercase tracking-wider">Total Days</p>
              <p className="text-2xl font-bold text-primary-900">{stats.total}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow-sm border border-primary-200 p-4">
          <div className="flex items-center">
            <div className="p-2 bg-green-100 rounded-lg">
              <CheckCircleIcon className="w-6 h-6 text-green-600" />
            </div>
            <div className="ml-3">
              <p className="text-xs text-primary-500 uppercase tracking-wider">Present</p>
              <p className="text-2xl font-bold text-green-600">{stats.present}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow-sm border border-primary-200 p-4">
          <div className="flex items-center">
            <div className="p-2 bg-red-100 rounded-lg">
              <XCircleIcon className="w-6 h-6 text-red-600" />
            </div>
            <div className="ml-3">
              <p className="text-xs text-primary-500 uppercase tracking-wider">Absent</p>
              <p className="text-2xl font-bold text-red-600">{stats.absent}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow-sm border border-primary-200 p-4">
          <div className="flex items-center">
            <div className="p-2 bg-yellow-100 rounded-lg">
              <ClockIcon className="w-6 h-6 text-yellow-600" />
            </div>
            <div className="ml-3">
              <p className="text-xs text-primary-500 uppercase tracking-wider">Late</p>
              <p className="text-2xl font-bold text-yellow-600">{stats.late}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Filter */}
      <div className="bg-white rounded-xl shadow-sm border border-primary-200 p-4 mb-8">
        <div className="flex flex-wrap items-end gap-4">
          <div>
            <label className="block text-sm font-medium text-primary-700 mb-1">From</label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="rounded-lg border-primary-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-primary-700 mb-1">To</label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="rounded-lg border-primary-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
            />
          </div>
          <button
            onClick={() => { setStartDate(''); setEndDate(''); }}
            className="px-4 py-2 bg-primary-100 text-primary-700 rounded-lg hover:bg-primary-200 transition-colors"
          >
            Clear Filters
          </button>
        </div>
      </div>

      {/* Attendance Table */}
      {filteredAttendance.length === 0 ? (
        <div className="bg-white rounded-xl shadow-sm border border-primary-200 p-8 text-center">
          <CalendarIcon className="w-16 h-16 text-primary-300 mx-auto" />
          <p className="text-primary-500 mt-4 text-lg">No attendance records found.</p>
          {attendance.length > 0 && (
            <p className="text-primary-400 text-sm mt-1">Try adjusting the date filters.</p>
          )}
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-sm border border-primary-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-primary-200">
              <thead className="bg-primary-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-primary-500 uppercase tracking-wider border-r border-primary-200">
                    #
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-primary-500 uppercase tracking-wider border-r border-primary-200">
                    Date
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-primary-500 uppercase tracking-wider border-r border-primary-200">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-primary-500 uppercase tracking-wider">
                    Remarks
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-primary-100">
                {filteredAttendance.map((record, index) => (
                  <tr key={record.id} className="hover:bg-primary-50 transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-primary-600 border-r border-primary-100">
                      {index + 1}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-primary-900 border-r border-primary-100">
                      {formatDate(record.date)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap border-r border-primary-100">
                      <StatusBadge status={record.status} />
                    </td>
                    <td className="px-6 py-4 text-sm text-primary-600">
                      {record.remarks || '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}